import { type CycleData, Interval, IntervalOptions } from "@qendo/schedule";
import { useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { Only } from "~/components/permission/Only";
import { itemWidth, setWidthOnLayout } from "~/util/layout";

export type ScheduleCycleSelectorProps = {
    value?: CycleData | null | undefined;
    onChange?: (cycle: CycleData) => void;
    disabled?: boolean;
};

const getMax = (mode: Interval): number => (mode === Interval.week ? 52 : 99);
const clampValue = (mode: Interval, value?: number | undefined): number => {
    return Math.min(getMax(mode), Math.max(value ?? 1, 1));
};

export const ScheduleCycleSelector = ({ value, onChange, disabled }: ScheduleCycleSelectorProps) => {
    const interval = value?.every ?? null;
    const mode = interval ?? Interval.day;
    const [width, setWidth] = useState(300);
    const onLayout = setWidthOnLayout(setWidth);
    const size = Math.floor(itemWidth(width, 2));

    const options = useMemo(() => {
        const word = mode === Interval.week ? "week" : "day";
        const max = getMax(mode);

        return [...Array.from({ length: max }).keys()].map((item) => {
            const value = item + 1;

            return {
                value,
                label: `${value} ${word}${item === 0 ? "" : "s"}`,
            };
        });
    }, [mode]);

    const use_for = clampValue(mode, value?.use_for);
    const pause_for = clampValue(mode, value?.pause_for);

    const setEvery = (every: Interval) => {
        onChange?.({
            every,
            use_for: clampValue(every, value?.use_for),
            pause_for: clampValue(every, value?.pause_for),
        });
    };

    const setRange = (range: "use_for" | "pause_for", val: number) => {
        const other = range === "use_for" ? "pause_for" : "use_for";

        // @ts-expect-error
        onChange?.({ every: mode, [range]: clampValue(mode, val), [other]: clampValue(mode, value?.[other]) });
    };

    return (
        <>
            <RadioSelector
                title="Every"
                options={IntervalOptions}
                value={interval}
                onValueChange={setEvery}
                disabled={disabled}
                columns={2}
            />
            <Only when={interval}>
                <View style={styles.range} onLayout={onLayout}>
                    <ComboBox
                        label="Use for"
                        items={options}
                        value={use_for}
                        onItemSelected={(v: number) => setRange("use_for", v)}
                        style={{ width: size }}
                        disabled={disabled}
                        searchable={false}
                    />
                    <ComboBox
                        label="Pause for"
                        items={options}
                        value={pause_for}
                        onItemSelected={(v: number) => setRange("pause_for", v)}
                        style={{ width: size }}
                        disabled={disabled}
                        searchable={false}
                    />
                </View>
            </Only>
        </>
    );
};

const styles = StyleSheet.create({
    range: {
        flexDirection: "row",
        gap: 12,
    },
});
